import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '../../../store/configureStore';
import { ProjectsResponse } from '../../../hooks/useInternalProjects';

import Conditional from '../../common/conditional';
import Modal from '../modal';

import { getTutorials } from '../../../store/tutorials';
import { getCurrentUser } from '../../../services/authService';

import { Project, User } from '../../../utils/models';
import { PROJECT_STATUS, tutorialConstants } from '../../../utils/constants';
import { getAllProjects } from '../../../services/projectService';

import styles from './style.module.scss';
import withProjects from '../../hoc/withProjects';

const AwaitingActivation: React.FC = () => {
	const [isFetching, setFetching] = useState(false);
	const [inactiveProjects, setInactiveProjects] = useState<Project[]>([]);

	const navigate = useNavigate();

	const { activeTutorial } = useAppSelector(getTutorials);
	const { firstName, role } = getCurrentUser() as User;

	const handleViewProject = (project: Project): void => {
		const path: string = `/dashboard/projects/${project._id}`;
		navigate(path);
	};

	const renderShimmers = () => {
		return [1, 2, 3].map((item) => (
			<div className={styles.awaitingActivationShimmer} key={item}></div>
		));
	};

	useEffect(() => {
		async function getInactiveProjects() {
			try {
				setFetching(true);

				const response = await getAllProjects<ProjectsResponse>({ params: { page: 1, projectStatuses: PROJECT_STATUS.AWAITING_ACTIVATION } });
				setInactiveProjects(response.projects);
			} catch (error) {
				console.log(error);
			} finally {
				setFetching(false);
			}
		}

		if (statuses.indexOf(activeTutorial!) && role.approveReports) {
			getInactiveProjects();
		}
	}, [role.approveReports, activeTutorial]);

	return (
		<Modal
			isVisible={inactiveProjects.length > 0}
			onClose={() => setInactiveProjects([])}
			style={{ maxWidth: '80%' }}
		>
			<Conditional isVisible={isFetching}>{renderShimmers()}</Conditional>

			<Conditional isVisible={!isFetching}>
				<div className={styles.awaitingActivation}>
					<h3>
						<i className="bi bi-exclamation-triangle-fill"></i>
						These projects need your attention, {firstName}!
					</h3>

					<div className={classNames([styles.awaitingActivationContainer, 'table-responsive'])}>
						<table className={styles.awaitingActivationTable}>
							<thead>
								<tr>
									<th>Date</th>
									<th>Title</th>
									<th>Organization</th>
									<th>Supervisor</th>
									<th></th>
								</tr>
							</thead>

							<tbody>
								{inactiveProjects.map((project) => (
									<tr key={project._id}>
										<td>
											<div className={styles.awaitingActivationDate}>
												{project.createdAt}
											</div>
										</td>
										
										<td>{project.projectTitle}</td>
										
										<td>
											<div
												className={styles.awaitingActivationOrganization}
											>
												{project.organisationName}
											</div>
										</td>

										<td>
											<div className={styles.awaitingActivationSupervisor}>
												{project.supervisorName}
											</div>
										</td>
										
										<td>
											<button className={styles.awaitingActivationAction} onClick={() => handleViewProject(project)}>
												Validate
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</Conditional>
		</Modal>
	);
};

const statuses: number[] = [
	tutorialConstants.WELCOME,
	tutorialConstants.INTRODUCTION,
	tutorialConstants.INTERNAL_DASHBOARD,
];

export default withProjects(AwaitingActivation);
